/**
 * Start somebody's offboarding (PRD 9.1): who is leaving, why, and their last
 * day -- and who does each department's part, as for an onboarding.
 *
 * The template is never asked for. The reason decides it
 * (`offboardingTemplateFor()`): a termination is the high-speed one, anything
 * else the standard one. The leaver is picked from Employees, which fills in
 * their site, role and manager from their onboarding; somebody who predates
 * utuwrk is typed. The worker checks everything again (`checkStart()`).
 *
 * A departure that is not a termination can ask for access to go at a time
 * on the last day, on the site's clock; blank is the end of the day. The
 * cards that revoke access fall due then, and show it beside when they were
 * done.
 *
 * Somebody staying who loses only some access starts here too, as the
 * client's form has it ("employee staying"): the access change, which has
 * none of HR's separation cards and never counts as leaving.
 */

import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  ACCESS_CHANGE,
  EMPLOYEE_TYPES,
  EMPLOYMENT_STATUSES,
  MANAGER_DEPARTMENT,
  OFFBOARDING_TRIGGERS,
  departmentsIn,
  offboardingTemplateFor,
  type Answer,
  siteSettings,
  takesCutoff,
  type Assignee,
  type EmployeeSummary,
  type StartResponse,
  testsField,
  type ProfileKey,
} from "@utuwrk/core";
import { sentence } from "../lib/api.js";
import { useOrg } from "../lib/org.js";
import { boardOf, go } from "../lib/router.js";
import { dayText, words } from "../lib/words.js";
import { Link, Loading, PageHeader, Problem, useLoad } from "./bits.js";
import { Questions } from "./Questions.js";
import { Started } from "./Started.js";
import { Segmented } from "@vcnet/ui";

/** Somebody typed in, rather than picked from the index. */
const NOT_LISTED = "";

const today = (): string => new Date().toISOString().slice(0, 10);
const twoWeeksOut = (): string => new Date(Date.now() + 14 * 86_400_000).toISOString().slice(0, 10);

/** Somebody can be offboarded unless they are being already, have gone, or never came. */
const canLeave = (employee: EmployeeSummary): boolean =>
  employee.state === "onboarding" || employee.state === "active";

export function StartOffboarding({
  employeeId,
  trigger: firstTrigger,
}: {
  employeeId?: string | undefined;
  trigger?: string | undefined;
}) {
  const { api, people, departments: listed } = useOrg();
  const { data: setup, error } = useLoad(() => api.setup(), [api]);
  const { data: index, error: indexError } = useLoad(() => api.employees(), [api]);

  const [picked, setPicked] = useState(employeeId ?? NOT_LISTED);
  const [typedName, setTypedName] = useState("");
  const [staying, setStaying] = useState(firstTrigger === ACCESS_CHANGE);
  const [reason, setReason] = useState<string>(
    OFFBOARDING_TRIGGERS.find((option) => option === firstTrigger) ?? OFFBOARDING_TRIGGERS[0],
  );
  const trigger = staying ? ACCESS_CHANGE : reason;
  const [lastDay, setLastDay] = useState(twoWeeksOut);
  const [cutoff, setCutoff] = useState("");
  const [siteId, setSiteId] = useState("");
  const [employeeType, setEmployeeType] = useState<string>("office");
  const [employmentStatus, setEmploymentStatus] = useState<string>("full-time");
  const [department, setDepartment] = useState("");
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [started, setStarted] = useState<StartResponse | null>(null);

  const leavers = useMemo(() => (index?.employees ?? []).filter(canLeave), [index]);
  const leaver = leavers.find((employee) => employee.id === picked);
  const sites = useMemo(() => setup?.levels.filter((level) => level.kind === "site") ?? [], [setup]);
  const site = siteId || sites[0]?.id || "";
  const atSite = setup?.departmentsAt[site] ?? listed;
  // The site's own template for this kind of departure, else the starter (core's offboardingFor()).
  const template = offboardingTemplateFor(trigger, setup?.templates ?? [], site);
  const urgent = template.use === "termination";
  const questions = template.questions ?? [];
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const unanswered = questions.some((question) => answers[question.key] === undefined);
  // The zone the time is read in: the site's, as every deadline at it is.
  const timeZone = useMemo(() => {
    const levels = setup?.levels ?? [];
    const own = levels.find((level) => level.id === site);
    const company = levels.find((level) => level.id === own?.parentId);
    const global = levels.find((level) => level.kind === "global");
    return siteSettings([global?.settings ?? {}, company?.settings ?? {}, own?.settings ?? {}]).timeZone;
  }, [setup, site]);
  const asksTime = takesCutoff(template);
  const departments = useMemo(() => {
    const all = departmentsIn(template);
    return [
      ...all.filter((name) => name === MANAGER_DEPARTMENT),
      ...all.filter((name) => name !== MANAGER_DEPARTMENT),
    ];
  }, [template]);
  const tests = (field: ProfileKey) => testsField(template, field);
  const active = people.filter((person) => person.active);

  // Picking somebody fills in what their onboarding already said, so nobody types it twice.
  useEffect(() => {
    if (!leaver) return;
    if (leaver.siteId) setSiteId(leaver.siteId);
    if (leaver.employeeType) setEmployeeType(leaver.employeeType);
    if (leaver.employmentStatus) setEmploymentStatus(leaver.employmentStatus);
    setDepartment(leaver.department);
    setChosen(leaver.managerId ? { [MANAGER_DEPARTMENT]: leaver.managerId } : {});
  }, [leaver]);

  // A termination is today's: the clock that matters runs in minutes from now.
  useEffect(() => {
    setLastDay(urgent ? today() : twoWeeksOut());
  }, [urgent]);

  const defaultFor = (name: string): string => setup?.staffing[site]?.[name]?.primary ?? "";
  const holderOf = (name: string): string => chosen[name] ?? defaultFor(name);
  const name = leaver ? leaver.name : typedName.trim();

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(null);
    const assignees: Record<string, Assignee> = {};
    for (const part of departments) {
      if (chosen[part] === undefined || chosen[part] === defaultFor(part)) continue;
      assignees[part] = {
        primary: chosen[part] || null,
        backup: setup?.staffing[site]?.[part]?.backup ?? null,
      };
    }
    try {
      setStarted(
        await api.start({
          templateId: template.id,
          siteId: site,
          trigger,
          employeeId: leaver?.id ?? null,
          employeeName: name,
          employeeType,
          employmentStatus,
          department,
          anchorDate: lastDay,
          cutoffTime: asksTime && cutoff ? cutoff : null,
          assignees,
          answers: Object.fromEntries(questions.map((question) => [question.key, answers[question.key]!])),
        }),
      );
    } catch (failure) {
      setProblem(sentence(failure));
    } finally {
      setBusy(false);
    }
  }

  if (!(setup && index) && !error && !indexError) return <Loading title="Start an offboarding" />;

  if (started) {
    return (
      <section className="page">
        <PageHeader title={`${name}’s ${staying ? "access change" : "offboarding"} has started`} />
        <Started
          started={started}
          lede={
            urgent
              ? "IT and Facilities have been told to revoke access now. The rest opens as the work before it finishes."
              : "The first cards are open and their owners have been told. Everything else opens as the work before it finishes."
          }
          openBoard={() => go(boardOf(started.id, "offboarding"))}
        />
      </section>
    );
  }

  return (
    <section className="page">
      <PageHeader
        back={{ to: { name: "offboardings" }, label: "Offboarding" }}
        title={staying ? "Change somebody’s access" : "Start an offboarding"}
        meta="Every department is told when its part is ready. Nothing goes out until you press Start."
      />
      <form className="panel" onSubmit={submit}>
        <Problem>{error ?? indexError}</Problem>
        {sites.length === 0 && setup ? (
          <p className="notice-line warn">
            There is no site yet. An admin adds one in <Link to={{ name: "settings" }}>Settings</Link>.
          </p>
        ) : null}

        <Segmented<"leaving" | "staying">
          label="Are they leaving?"
          value={staying ? "staying" : "leaving"}
          options={[
            { value: "leaving", label: "Leaving" },
            { value: "staying", label: "Staying, some access removed" },
          ]}
          onChange={(value) => setStaying(value === "staying")}
        />

        <div className="controls section-gap">
          <label className="field">
            <span>{staying ? "Whose access is changing" : "Who is leaving"}</span>
            <select value={picked} onChange={(event) => setPicked(event.target.value)}>
              <option value={NOT_LISTED}>Somebody not listed…</option>
              {leavers.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.name}
                  {employee.department ? ` (${employee.department})` : ""}
                </option>
              ))}
            </select>
          </label>
          {!leaver ? (
            <label className="field">
              <span>Their name</span>
              <input
                value={typedName}
                onChange={(event) => setTypedName(event.target.value)}
                placeholder="Dana Reyes"
                autoComplete="off"
              />
            </label>
          ) : null}
          {staying ? null : (
            <label className="field">
              <span>Why they are leaving</span>
              <select value={reason} onChange={(event) => setReason(event.target.value)}>
                {OFFBOARDING_TRIGGERS.map((option) => (
                  <option key={option} value={option}>
                    {words(option)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="field">
            <span>{staying ? "Day access goes" : "Last day"}</span>
            <input
              type="date"
              value={lastDay}
              onChange={(event) => event.target.value && setLastDay(event.target.value)}
            />
          </label>
          {asksTime ? (
            <label className="field">
              <span>Access goes at (site time)</span>
              <input type="time" value={cutoff} onChange={(event) => setCutoff(event.target.value)} />
            </label>
          ) : null}
          <label className="field">
            <span>Site</span>
            <select value={site} onChange={(event) => setSiteId(event.target.value)}>
              {sites.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </label>
          {tests("employeeType") ? (
            <label className="field">
              <span>Kind of role</span>
              <select value={employeeType} onChange={(event) => setEmployeeType(event.target.value)}>
                {EMPLOYEE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {words(type)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {tests("employmentStatus") ? (
            <label className="field">
              <span>Status</span>
              <select value={employmentStatus} onChange={(event) => setEmploymentStatus(event.target.value)}>
                {EMPLOYMENT_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {words(status)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <label className="field">
            <span>Their department</span>
            <select value={department} onChange={(event) => setDepartment(event.target.value)}>
              <option value="">No department</option>
              {(department && !atSite.includes(department) ? [department, ...atSite] : atSite).map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <p className={`notice-line${urgent ? " warn" : ""}`}>
          {urgent
            ? "This uses the high-speed template: IT and Facilities are told to revoke access the moment you press Start, and system access is due within 30 minutes."
            : (staying
                ? `Their manager lists what goes, and only that is removed on ${dayText(lastDay)}. None of HR's separation cards apply, and they stay in Employees as they are. `
                : `This uses the ${template.name.toLowerCase()} template, counted back from ${dayText(lastDay)}. `) +
              (cutoff
                ? `System and building access are due to go at ${cutoff} that day, ${timeZone.replace(/_/g, " ")} time.`
                : "Without a time, access is due by the end of that day.")}
        </p>

        <Questions
          questions={questions}
          answers={answers}
          onChange={(key, answer) => setAnswers((was) => ({ ...was, [key]: answer }))}
        />

        <h2 className="section-title section-gap">Who does each part</h2>
        <p className="muted">
          Filled in from the site&rsquo;s defaults. Change any of them for this{" "}
          {staying ? "change" : "departure"}; the defaults stay as they are.
        </p>
        <div className="controls">
          {departments.map((part) => (
            <label key={part} className="field">
              <span>{part === MANAGER_DEPARTMENT ? "Their manager" : part}</span>
              <select
                value={holderOf(part)}
                onChange={(event) => setChosen({ ...chosen, [part]: event.target.value })}
              >
                <option value="">Nobody: anyone in {part}</option>
                {[
                  ...active.filter((person) => person.department === part),
                  ...active.filter((person) => person.department !== part),
                ].map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name || person.email}
                    {person.department && person.department !== part ? ` (${person.department})` : ""}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>

        <div className="action-row section-gap">
          <button className="button" type="submit" disabled={busy || !name || !site || unanswered}>
            Start
          </button>
          <Link to={{ name: "template", id: template.id }} className="button button-quiet">
            Preview the template first
          </Link>
        </div>
        <Problem>{problem}</Problem>
      </form>
    </section>
  );
}
